/*
 * Booking from an auto quote. A quote priced by the auto quoter (a home from the price list, or a lawn) keeps its answers.
 * On the quote page the client changes them (fewer rooms to pay less, more add-ons, another schedule) and books a day,
 * which puts the job on the schedule. The old Steward did this on its quote page (quotelive.php, quoteweb.php quote-book).
 *
 * Answers moved from the WordPress Steward keep its shapes (webJob for a home, lawnJob for a lawn); quoteAnswersOf reads
 * both, so a moved quote can be changed and booked too.
 */
import { z } from "zod";
import { lawnAnswersShape, LAWN_HEDGES, type LawnAnswers, type LawnHedges } from "./lawn-prices.js";
import { hourlyAnswersShape, PRICE_OFTEN, type HomeAnswers, type HourlyAnswers, type PriceOften } from "./prices.js";
import { TIMES_OF_DAY, homeAnswersShape } from "./website.zod.js";

export type QuoteAnswers =
  { kind: "home"; a: HomeAnswers } | { kind: "lawn"; a: LawnAnswers } | { kind: "hourly"; a: HourlyAnswers & { kind: "hourly" } };

const rec = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : Number(v) || 0);

/** The auto quote answers on a quote, in this app's shapes, or null for a quote priced by hand. */
export function quoteAnswersOf(raw: unknown): QuoteAnswers | null {
  const r = rec(raw);
  const lawn = lawnAnswersShape.safeParse(r);
  if (lawn.success) return { kind: "lawn", a: lawn.data };
  const hourly = hourlyAnswersShape.safeParse(r);
  if (hourly.success) return { kind: "hourly", a: hourly.data };
  const home = homeAnswersShape.safeParse(r);
  if (home.success) return { kind: "home", a: home.data };
  if (r.webJob) {
    const w = rec(r.webJob);
    const extras: Record<string, boolean | number> = {};
    if (Array.isArray(w.extras)) for (const id of w.extras) if (typeof id === "string") extras[id] = true;
    for (const [k, v] of Object.entries(rec(w.extras))) if (typeof v === "boolean" || typeof v === "number") extras[k] = v;
    if (n(w.windows) > 0) extras.windows = Math.trunc(n(w.windows));
    const often = PRICE_OFTEN.includes(w.freq as PriceOften) ? (w.freq as PriceOften) : "once";
    const parsed = homeAnswersShape.safeParse({
      serviceId: typeof w.service === "string" ? w.service : "",
      sizeBy: w.sizeMode === "sqft" ? "sqft" : "rooms",
      beds: Math.min(20, Math.trunc(n(w.beds))),
      baths: Math.min(20, n(w.baths)),
      other: Math.min(20, Math.trunc(n(w.other))),
      sqft: Math.min(50_000, Math.trunc(n(w.sqft))),
      often,
      extras,
    });
    return parsed.success ? { kind: "home", a: parsed.data } : null;
  }
  if (r.lawnJob) {
    const w = rec(r.lawnJob);
    const services = Array.isArray(w.services) ? w.services : [];
    if (!services.includes("mow")) return null;
    const parsed = lawnAnswersShape.safeParse({
      kind: "lawn",
      sqft: Math.trunc(n(w.sqft)),
      often: ["weekly", "biweekly", "onetime"].includes(String(w.freq)) ? w.freq : "biweekly",
      condition: ["normal", "tall", "overgrown"].includes(String(w.cond)) ? w.cond : "normal",
      bagging: w.bag === true || w.bag === "true",
      hedges: services.includes("hedges") && LAWN_HEDGES.includes(w.hedges as LawnHedges) ? w.hedges : null,
      weeds: services.includes("weeds"),
    });
    return parsed.success ? { kind: "lawn", a: parsed.data } : null;
  }
  return null;
}

/** Rooms the home counts (bedrooms, bathrooms with a half as one, other rooms), or its square feet. */
const homeSize = (a: HomeAnswers) => (a.sizeBy === "sqft" ? a.sqft : a.beds + Math.ceil(a.baths) + a.other);

/** The client made the home smaller than we quoted: they must say which rooms not to clean (old quotelive.php). */
export function smallerHome(sent: HomeAnswers | null, now: HomeAnswers): boolean {
  if (sent?.sizeBy !== now.sizeBy) return false;
  return homeSize(now) < homeSize(sent);
}

/** The arrival window for a time of day, the website's hours (8 AM to 5 PM). Flexible has none. */
export const ARRIVAL: Record<(typeof TIMES_OF_DAY)[number], { from: string; to: string } | null> = {
  morning: { from: "08:00", to: "12:00" },
  afternoon: { from: "12:00", to: "17:00" },
  flexible: null,
};

/** How many days ahead the quote page offers, from tomorrow: 4 weeks (This week, Next week, In 2 weeks, In 3 weeks). */
export const BOOK_DAYS = 28;

/**
 * Whether the morning and the afternoon of a day have room. Each half holds half the day's hours. A visit counts in the
 * half it starts in; one with no start time (Anytime) counts half in each. A half has room while it is not full.
 */
export function halfDays(
  visits: readonly { start: string | null; hours: number }[],
  hoursHeld: number,
): { morning: boolean; afternoon: boolean } {
  let am = 0;
  let pm = 0;
  for (const v of visits) {
    const hour = v.start ? Number(v.start.slice(0, 2)) : null;
    if (hour === null || !Number.isFinite(hour)) {
      am += v.hours / 2;
      pm += v.hours / 2;
    } else if (hour < 12) am += v.hours;
    else pm += v.hours;
  }
  const half = hoursHeld / 2;
  return { morning: am < half, afternoon: pm < half };
}

const bookDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a day.");

export const quoteBookInput = z
  .object({
    /** The day picked, or left out for Pick a day for me. */
    day: bookDay.optional(),
    /**
     * Pick a day for me (owner, 2026-11-02): the dates they can do. The soonest open day in them with room at their arrival
     * time is booked.
     */
    pickFrom: bookDay.optional(),
    pickTo: bookDay.optional(),
    timeOfDay: z.enum(TIMES_OF_DAY).default("flexible"),
    /** Any of our cleaners, not the one they had last (more days open). */
    anyCleaner: z.boolean().default(false),
    /** Rooms not to clean when the home was made smaller, and anything else for the crew. */
    note: z.string().trim().max(600).default(""),
  })
  .strict()
  .refine((b) => Boolean(b.day) || Boolean(b.pickFrom && b.pickTo && b.pickFrom <= b.pickTo), {
    message: "Pick a day, or the dates that work for you.",
    path: ["day"],
  });
export type QuoteBookInput = z.infer<typeof quoteBookInput>;

const priced = z.object({ id: z.string(), label: z.string(), cents: z.number().nullable() });

/** What the quote page needs to change an auto quote and book it. */
export const publicAutoQuote = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("home"),
    answers: homeAnswersShape,
    /** The home as first quoted, to tell when it got smaller. */
    sent: homeAnswersShape.nullable(),
    /** Each clean on the list, priced for this home (first visit, add-ons left out). */
    services: z.array(priced.extend({ repeats: z.boolean(), sqftOk: z.boolean() })),
    /** Each schedule for the picked clean: what every visit after the first costs. */
    often: z.array(priced),
    /** cents: the price for one at the schedule picked (the plan price on a plan); listCents: the usual price then. */
    extras: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        cents: z.number(),
        listCents: z.number().nullable().default(null),
        each: z.boolean(),
      }),
    ),
    /** The first visit is a plan visit: the client had a standard, deep or move clean lately (hub-booking.ts). */
    planStart: z.boolean().default(false),
    /** A one time clean booked again soon: the plan price, shown first (hub-booking.ts). */
    nudge: z
      .object({ serviceId: z.string(), id: z.string(), label: z.string(), perVisitCents: z.number() })
      .nullable()
      .default(null),
  }),
  z.object({
    kind: z.literal("hourly"),
    answers: hourlyAnswersShape,
    /** One cleaner's hour. */
    rateCents: z.number(),
    /** Each schedule, priced per visit for these cleaners and hours. */
    often: z.array(priced),
  }),
  z.object({
    kind: z.literal("lawn"),
    answers: lawnAnswersShape,
    /** Each schedule: the first visit, and every visit after it. */
    often: z.array(priced.extend({ perVisitCents: z.number().nullable() })),
    hedges: z.array(priced),
    baggingCents: z.number(),
    weedsCents: z.number(),
  }),
]);
export type PublicAutoQuote = z.infer<typeof publicAutoQuote>;

export const publicBooking = z.object({
  /** A card is saved first to hold the day. */
  cardFirst: z.boolean(),
  /** From tomorrow, BOOK_DAYS days. Open is false when the day is full or nobody works; morning and afternoon each half. */
  days: z.array(
    z.object({
      date: z.string(),
      open: z.boolean(),
      morning: z.boolean(),
      afternoon: z.boolean(),
      /** Their own cleaner can take them that day (open, nobody on the crew off, room left). True when there is none. */
      withCleaner: z.boolean().default(true),
    }),
  ),
  /** The cleaner they had last ("Kenia"), whose open days the page shows first; null when there is none. */
  cleaner: z.string().nullable().default(null),
});
export type PublicBooking = z.infer<typeof publicBooking>;
